import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { EM_DASH, FieldKey } from "~/pages/Compliance/ProposedChangesModal/constants";
import { formatRiskRating, getEntityType } from "~/pages/Compliance/ProposedChangesModal/utils";
import { AML_ENTITY_TYPE_ID_TO_NAME } from "~/pages/Compliance/utils/amlTypeIdUtil";
import { formatShortFormDate } from "~/utils/datetime";
import { makeRow, type SectionArgs } from "./index";
import type { ProfileData } from "~/pages/Compliance/ProposedChangesModal/types";

const strValue = (v: string | null | undefined) => v || EM_DASH;
const dateValue = (v: string | null | undefined) => formatShortFormDate(v ?? null) || EM_DASH;

const anyDifferent = (values: Array<string | null | undefined>): boolean => {
  const first = values[0] || "";
  return values.some((v) => (v || "") !== first);
};

const appendRiskRatingRow = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
}: SectionArgs) => {
  const cur = formatRiskRating(currentProfile.riskRating);
  const pub = publishedProfile ? formatRiskRating(publishedProfile.riskRating) : EM_DASH;
  const prop = formatRiskRating(proposedProfile.riskRating);
  if (cur === prop && (!hasPublished || cur === pub)) return;
  rows.push(
    makeRow({
      rowKey: FieldKey.RiskRating,
      fieldKey: FieldKey.RiskRating,
      field: "Manual override risk rating",
      current: cur,
      recentlyPublished: pub,
      proposed: prop,
      hasPublished,
    }),
  );
};

const amlEntityTypeName = (profile: ProfileData | undefined): string => {
  const id = profile?.amlEntityTypeId;
  if (id == null) return EM_DASH;
  return AML_ENTITY_TYPE_ID_TO_NAME[id] || EM_DASH;
};

const appendAmlEntityTypeRow = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
}: SectionArgs) => {
  const curId = currentProfile.amlEntityTypeId ?? null;
  const pubId = publishedProfile?.amlEntityTypeId ?? null;
  const propId = proposedProfile.amlEntityTypeId ?? null;
  if (curId === propId && (!hasPublished || curId === pubId)) return;
  rows.push(
    makeRow({
      rowKey: FieldKey.AmlQuestion,
      fieldKey: FieldKey.AmlQuestion,
      field: "AML entity type",
      current: amlEntityTypeName(currentProfile),
      recentlyPublished: hasPublished ? amlEntityTypeName(publishedProfile) : EM_DASH,
      proposed: amlEntityTypeName(proposedProfile),
      hasPublished,
    }),
  );
};

export const appendScalarRows = ({
  rows,
  currentProfile,
  publishedProfile,
  proposedProfile,
  hasPublished,
  isIndividual,
  isJoint,
}: SectionArgs) => {
  const addString = (
    field: string,
    fieldKey: FieldKey,
    rowKey: string,
    cur: string | null | undefined,
    pub: string | null | undefined,
    prop: string | null | undefined,
  ) => {
    const compared = [cur, hasPublished ? pub : cur, prop];
    if (!anyDifferent(compared)) return;
    rows.push(
      makeRow({
        rowKey,
        fieldKey,
        field,
        current: strValue(cur),
        recentlyPublished: hasPublished ? strValue(pub) : EM_DASH,
        proposed: strValue(prop),
        hasPublished,
      }),
    );
  };

  const addDate = (
    field: string,
    fieldKey: FieldKey,
    rowKey: string,
    cur: string | null | undefined,
    pub: string | null | undefined,
    prop: string | null | undefined,
  ) => {
    const compared = [cur, hasPublished ? pub : cur, prop];
    if (!anyDifferent(compared)) return;
    rows.push(
      makeRow({
        rowKey,
        fieldKey,
        field,
        current: dateValue(cur),
        recentlyPublished: hasPublished ? dateValue(pub) : EM_DASH,
        proposed: dateValue(prop),
        hasPublished,
      }),
    );
  };

  const appendAccountTypeRow = (field: string) => {
    const curType = getEntityType(currentProfile.accountType);
    const pubType = publishedProfile ? getEntityType(publishedProfile.accountType) : EM_DASH;
    const propType = getEntityType(proposedProfile.accountType);
    if (curType === propType && (!hasPublished || curType === pubType)) return;
    rows.push(
      makeRow({
        rowKey: FieldKey.AccountType,
        fieldKey: FieldKey.AccountType,
        field,
        current: curType,
        recentlyPublished: pubType,
        proposed: propType,
        hasPublished,
      }),
    );
  };

  if (isIndividual) {
    addString(
      "Name",
      FieldKey.LegalName,
      FieldKey.LegalName,
      getProfileName(currentProfile),
      publishedProfile ? getProfileName(publishedProfile) : null,
      getProfileName(proposedProfile),
    );
    appendAccountTypeRow("Account type");
    addDate(
      "Date of birth",
      FieldKey.DateOfBirth,
      FieldKey.DateOfBirth,
      currentProfile.dateOfBirth,
      publishedProfile?.dateOfBirth,
      proposedProfile.dateOfBirth,
    );
    addString(
      "EIN/TIN",
      FieldKey.TaxId,
      FieldKey.TaxId,
      currentProfile.taxIdUnencrypted,
      publishedProfile?.taxIdUnencrypted,
      proposedProfile.taxIdUnencrypted,
    );
    addString(
      "Tax ID type",
      FieldKey.TaxIdType,
      FieldKey.TaxIdType,
      currentProfile.taxIdType,
      publishedProfile?.taxIdType,
      proposedProfile.taxIdType,
    );
    appendRiskRatingRow({
      rows,
      currentProfile,
      publishedProfile,
      proposedProfile,
      hasPublished,
    });
    return;
  }

  addString(
    "Legal business name",
    FieldKey.LegalName,
    FieldKey.LegalName,
    currentProfile.legalName,
    publishedProfile?.legalName,
    proposedProfile.legalName,
  );

  appendAccountTypeRow("Entity type");

  // Joint profiles have no date-of-formation or tax-id fields on the backend input.
  if (!isJoint) {
    addDate(
      "Date of formation",
      FieldKey.DateOfOrganization,
      FieldKey.DateOfOrganization,
      currentProfile.dateOfOrganization,
      publishedProfile?.dateOfOrganization,
      proposedProfile.dateOfOrganization,
    );
    addString(
      "Identification number",
      FieldKey.TaxId,
      FieldKey.TaxId,
      currentProfile.taxIdUnencrypted,
      publishedProfile?.taxIdUnencrypted,
      proposedProfile.taxIdUnencrypted,
    );
    addString(
      "Tax ID type",
      FieldKey.TaxIdType,
      FieldKey.TaxIdType,
      currentProfile.taxIdType,
      publishedProfile?.taxIdType,
      proposedProfile.taxIdType,
    );
  }
  appendAmlEntityTypeRow({
    rows,
    currentProfile,
    publishedProfile,
    proposedProfile,
    hasPublished,
  });
  appendRiskRatingRow({
    rows,
    currentProfile,
    publishedProfile,
    proposedProfile,
    hasPublished,
  });
};
